import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fileSrc } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Check, X, Clock, Plus, Trash2, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import RecipientSelect, { useProjectRecipients } from "../RecipientSelect";

const STATUS_META = {
  pending: { c: "#C49A4A", icon: Clock, label: "Pending" },
  approved: { c: "#506B52", icon: Check, label: "Approved" },
  rejected: { c: "#9E4747", icon: X, label: "Rejected" },
};

export default function ApprovalsTab({ projectId, readOnly, canRespond = true }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", image_url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const { members, selected: recipients, setSelected: setRecipients } = useProjectRecipients(projectId);

  const load = () => api.get(`/projects/${projectId}/approvals`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description || "");
      fd.append("image_url", form.image_url || "");
      fd.append("recipients", recipients.join(","));
      if (file) fd.append("file", file);
      await api.post(`/projects/${projectId}/approvals`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ title: "", description: "", image_url: "" });
      setFile(null);
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadAttachment = (a) => {
    const att = a.attachment;
    if (!att?.data_url) return;
    window.open(fileSrc(att.data_url), "_blank", "noopener,noreferrer");
  };

  const decide = async (id, decision) => {
    await api.put(`/approvals/${id}`, { decision });
    toast.success(`Marked ${decision}`);
    load();
  };

  const remove = async (id) => { await api.delete(`/approvals/${id}`); load(); };

  return (
    <div className="max-w-4xl space-y-6" data-testid="approvals-tab">
      {!readOnly && (
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-approval-btn"
                className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
                <Plus size={15} className="mr-2" /> Request Approval
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
              <DialogHeader><DialogTitle className="font-serif text-2xl text-[#2B2622]">Request approval</DialogTitle><DialogDescription className="sr-only">Create a new approval request</DialogDescription></DialogHeader>
              <form onSubmit={create} className="space-y-4 mt-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Title</Label>
                  <Input data-testid="approval-title-input" required value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Description</Label>
                  <Textarea data-testid="approval-desc-input" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Image URL (optional)</Label>
                  <Input data-testid="approval-image-input" value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Attach document or image (optional)</Label>
                  <input data-testid="approval-file-input" type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="mt-1 block w-full text-sm text-[#5C5651] file:mr-3 file:border file:border-[#A68A6B] file:bg-transparent file:text-[#A68A6B] file:px-4 file:py-2 file:uppercase file:text-xs file:tracking-wider" />
                  {file && <p className="text-[11px] text-[#9b938b] mt-1 truncate">{file.name}</p>}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5651] block mb-1">Notify by email</Label>
                  <RecipientSelect members={members} selected={recipients} setSelected={setRecipients} />
                </div>
                <Button data-testid="create-approval-submit" type="submit" disabled={busy}
                  className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">{busy ? "Sending…" : "Send request"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {items.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center text-[#5C5651]">
          No approval requests yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-px bg-[#EBE5E0] border border-[#EBE5E0]">
          {items.map((a) => {
            const meta = STATUS_META[a.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <div key={a.id} className="bg-white p-6" data-testid="approval-card">
                {a.image_url && (
                  <img src={fileSrc(a.image_url)} alt={a.title} className="w-full h-40 object-cover mb-4 border border-[#EBE5E0]" />
                )}
                {a.attachment && a.attachment.is_image && (
                  <img src={fileSrc(a.attachment.data_url)} alt={a.title} className="w-full h-40 object-cover mb-4 border border-[#EBE5E0]" data-testid="approval-attachment-image" />
                )}
                {a.attachment && !a.attachment.is_image && (
                  <button data-testid="approval-attachment-doc" onClick={() => downloadAttachment(a)}
                    className="w-full flex items-center gap-3 border border-[#EBE5E0] bg-[#F9F8F6] p-3 mb-4 text-left hover:border-[#A68A6B]">
                    <FileText size={18} strokeWidth={1.5} className="text-[#A68A6B] shrink-0" />
                    <span className="text-sm text-[#2B2622] truncate flex-1">{a.attachment.filename}</span>
                    <Download size={14} strokeWidth={1.5} className="text-[#A68A6B] shrink-0" />
                  </button>
                )}
                <div className="flex items-center justify-between mb-2">
                  <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider" style={{ color: meta.c }}>
                    <Icon size={13} strokeWidth={2} /> {meta.label}
                  </span>
                  {!readOnly && (
                    <button data-testid={`delete-approval-${a.id}`} onClick={() => remove(a.id)} className="text-[#5C5651] hover:text-[#9E4747]">
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
                <h3 className="font-serif text-xl text-[#2B2622]">{a.title}</h3>
                <p className="text-sm text-[#5C5651] mt-1">{a.description}</p>
                {a.decided_by && <p className="text-[11px] text-[#9b938b] mt-2">{meta.label} by {a.decided_by}</p>}

                {a.status === "pending" && canRespond && (
                  <div className="flex gap-2 mt-4">
                    <Button data-testid={`approve-${a.id}`} onClick={() => decide(a.id, "approved")}
                      className="flex-1 rounded-none bg-[#506B52] hover:bg-[#3f553f] text-white uppercase tracking-wider text-xs">
                      <Check size={14} className="mr-1" /> Approve
                    </Button>
                    <Button data-testid={`reject-${a.id}`} onClick={() => decide(a.id, "rejected")}
                      className="flex-1 rounded-none bg-transparent border border-[#9E4747] text-[#9E4747] hover:bg-[#9E4747] hover:text-white uppercase tracking-wider text-xs">
                      <X size={14} className="mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
